import { Injectable } from '@angular/core';
import { SecurityContext } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import DOMPurify from 'dompurify';

@Injectable({
	providedIn: 'root',
})
export class SanitizationService {
	private static readonly ALLOWED_TAGS = [
		'p',
		'br',
		'strong',
		'b',
		'em',
		'i',
		'u',
		'a',
		'ul',
		'ol',
		'li',
		'blockquote',
		'table',
		'thead',
		'tbody',
		'tr',
		'th',
		'td',
		'caption',
		'figure',
		'figcaption',
		'span',
		'img',
	];

	private static readonly ALLOWED_ATTR = [
		'href',
		'target',
		'rel',
		'colspan',
		'rowspan',
		'style',
		'class',
		'src',
		'alt',
		'width',
		'height',
	];

	// Elements whose end is a word boundary when HTML is flattened to text.
	private static readonly TEXT_BOUNDARY_SELECTOR =
		'p, div, br, li, tr, td, th, h1, h2, h3, h4, h5, h6, blockquote, figcaption, caption';

	constructor(private readonly sanitizer: DomSanitizer) {}

	escapeText(value: string): string {
		return (value ?? '')
			.replace(/&/g, '&amp;')
			.replace(/</g, '&lt;')
			.replace(/>/g, '&gt;')
			.replace(/"/g, '&quot;')
			.replace(/'/g, '&#x27;');
	}

	// Strips all markup and returns the plain text content of an HTML string.
	// Use for list/grid previews of rich-text fields so stored HTML/JS cannot
	// execute and truncation cannot split a tag. Escape the result before
	// inserting it into innerHTML.
	htmlToText(html: string): string {
		const doc = new DOMParser().parseFromString(html ?? '', 'text/html');
		// textContent carries no whitespace at block boundaries, so "<p>A</p><p>B</p>"
		// would read "AB". Mark each boundary with a space, then collapse the runs.
		doc.body.querySelectorAll(SanitizationService.TEXT_BOUNDARY_SELECTOR).forEach((el) => el.after(' '));
		return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
	}

	sanitizeHtml(html: string): string {
		return DOMPurify.sanitize(html ?? '', {
			ALLOWED_TAGS: SanitizationService.ALLOWED_TAGS,
			ALLOWED_ATTR: SanitizationService.ALLOWED_ATTR,
			ADD_DATA_URI_TAGS: ['img'],
		});
	}

	sanitizeUrl(url: string): string | null {
		if (!url) {
			return null;
		}
		return this.sanitizer.sanitize(SecurityContext.URL, url);
	}

	buildSafeHref(url: string | null | undefined): string {
		if (!url) return '';
		if (!/^https?:\/\//i.test(url)) return '';
		return this.escapeText(encodeURI(url));
	}

	trustResourceUrl(url: string): SafeResourceUrl | null {
		const sanitized = this.sanitizeUrl(url);
		if (!sanitized) {
			return null;
		}
		return this.sanitizer.bypassSecurityTrustResourceUrl(sanitized);
	}
}
